import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useState } from 'react';

import { gql } from '__generated__';

import { CopyLinkInput } from '../../common/Common/CopyLink';
import { EditAppPermissionsForm } from './EditPermissionsForm';
import { getServerErrors, isGraphQLAuthenticatorRequiredError } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import GetAppsForProjectQuery from './GetAppsForProjectQuery';
import UI from '../../common/UI';
import useAuthenticatorVerificationModal from '../../common/useAuthenticatorVerificationModal';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useValidation from '../../common/useValidation';

const addAppSchema = yup.object().shape({
  name: yup.string().required(),
  permissions: yup.array().min(1).required(),
});

export const AddAppMutation = gql(`
  mutation AddApp($input: AddAppInput!) {
    addApp(input: $input) {
      token
    }
  }
`);

export interface AddAppFormProps {
  onSuccess?: (token: string) => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const AddAppForm = ({ onSuccess, onCancel, refetchQueries = [GetAppsForProjectQuery] }: AddAppFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();

  const form = useForm({
    values: {
      integration_type: '',
      name: '',
      permissions: [],
    },
  });

  const [showAuthenticatorVerificationModal] = useAuthenticatorVerificationModal({
    onSuccess: () => addApp(),
  });

  const [addApp, { error, loading }] = useMutation(
    AddAppMutation, {
      variables: {
        input: {
          project_id: project.id,
          name: form.values.name,
          requested_permissions: form.values.permissions,
        },
      },
      onCompleted: ({ addApp: { token } }) => {
        notifier.success(t('notifications.app_added'));
        onSuccess?.(token);
      },
      onError: (error) => {
        if (isGraphQLAuthenticatorRequiredError(error)) {
          showAuthenticatorVerificationModal();
        }
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const { valid, errors } = useValidation({
    schema: addAppSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addApp()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('add_app')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_app_description')}
          </UI.Paragraph>
        </UI.Div>

        {!isGraphQLAuthenticatorRequiredError(error) && <UI.ServerErrors error={error} />}

        <UI.HR />

        <UI.InputGroup
          sc={{ valid: !errors.integration_type, touched: form.touched.integration_type, mb: 0 }}
        >
          <UI.InputLabel htmlFor="IntegrationFirstParty">
            {t('app_integration_type')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('app_integration_type_description')}
          </UI.InputDescription>
          <UI.Radio {...form.radio('integration_type', 'first_party')} id="IntegrationFirstParty">
            {t('app_integration_first_party')}
          </UI.Radio>
          <UI.Radio {...form.radio('integration_type', 'third_party')}>
            {t('app_integration_third_party')}
          </UI.Radio>
          <UI.ErrorMessages attribute={t('app_integration_type')} errors={errors.integration_type} />
        </UI.InputGroup>

        {form.values.integration_type === 'first_party' && (
          <>
            <UI.InputGroup
              sc={{ valid: !errors.name, touched: form.touched.name, mb: 0 }}
            >
              <UI.InputLabel htmlFor="Name">
                {t('name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.InputDescription>
                {t('app_name_description')}
              </UI.InputDescription>
              <UI.DebouncedInput id="Name" {...form.text('name')} />
              <UI.ErrorMessages attribute={t('name')} errors={errors.name} />
            </UI.InputGroup>

            <EditAppPermissionsForm
              form={form}
              errors={errors}
              inputLabel={t('requested_permissions')}
              inputDescription={t('requested_permissions_description')}
            />
          </>
        )}

        {form.values.integration_type === 'third_party' && (
          <UI.Warning>
            <Trans i18nKey="app_connection_instructions">
              <UI.A
                href="https://about.atleta.cc/article/api-connection"
                target="_blank"
                rel="noopener noreferrer"
              />
            </Trans>
          </UI.Warning>
        )}

        <UI.HR />

        <UI.Div sc={{ textAlign: 'right' }}>
          {onCancel && (
            <UI.Button onClick={() => onCancel()} sc={{ mr: 2 }}>
              {t('cancel')}
            </UI.Button>
          )}
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary', loading }}
            disabled={!valid || loading || form.values.integration_type !== 'first_party'}
          >
            {t('add_app')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface AppAddedProps {
  token: string;
  onSuccess?: () => void;
}

const AppAdded = ({ token, onSuccess }: AppAddedProps) => {
  const { t } = useTranslation();

  return (
    <UI.Div>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4>
            {t('access_token')}
          </UI.H4>
        </UI.Div>

        <UI.Div>
          <Trans i18nKey="app_access_token_description">
            <UI.Strong />
          </Trans>
        </UI.Div>

        <UI.InputGroup>
          <CopyLinkInput value={token} />
        </UI.InputGroup>

        <UI.Warning>
          <Trans i18nKey="app_access_token_warning">
            <UI.Strong />
          </Trans>
        </UI.Warning>

        <UI.HR />

        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            onClick={() => onSuccess?.()}
          >
            {t('ok')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Div>
  );
};

export interface AddAppModalProps {
  closeModal: () => void;
  refetchQueries?: DocumentNode[];
}

export const AddAppModal = ({ closeModal, refetchQueries }: AddAppModalProps) => {
  const [token, setToken] = useState<string | null>(null);

  return !token
    ? <AddAppForm onSuccess={setToken} onCancel={closeModal} refetchQueries={refetchQueries} />
    : <AppAdded token={token} onSuccess={closeModal} />;
};

const useAddAppModal = () => {
  const [openModal, closeModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeModal}>
      <AddAppModal closeModal={closeModal} />
    </UI.Modal>
  ), []);

  return [openModal, closeModal];
};

export default useAddAppModal;
